/**
 * Anvil combine / repair (vanilla AnvilMenu.createResult, Java): unit repair
 * with the material (25% per item), same-item combine (+12% bonus), merging
 * enchantments (same level +1 up to max, else the higher), per-enchantment
 * anvil cost (halved from books), prior-work penalty 2^n - 1 per input,
 * "Too Expensive!" at 40+. Renaming is not implemented.
 */

import { I, itemNameOf } from '../itemIds';
import { maxDamageOf } from '../itemdb';
import * as B from '../../blocks';
import { cloneStack, isEmpty, type Stack } from '../types';
import { canApply, compatible, enchDef } from './registry';
import { enchLevel, setEnch } from './stack';

export const TOO_EXPENSIVE = 40;

/** Vanilla 1.21 `anvil_cost` by weight: 10→1, 5→2, 2→4, 1→8. */
export function anvilCost(enchId: number): number {
  const w = enchDef(enchId)?.weight ?? 1;
  return w >= 10 ? 1 : w >= 5 ? 2 : w >= 2 ? 4 : 8;
}

const penalty = (rep: number | undefined): number => (1 << (rep ?? 0)) - 1;

/** The material that repairs an item (0 = none). */
export function repairMaterial(itemId: number): number {
  const n = itemNameOf(itemId) ?? '';
  if (n === 'SHIELD') return B.PLANKS_ID;
  if (n === 'ELYTRA') return I.PHANTOM_MEMBRANE; // vanilla: repaired with phantom membranes
  if (n === 'MACE') return I.BREEZE_ROD; // vanilla: repaired with breeze rods
  const mat = n.split('_')[0];
  switch (mat) {
    case 'WOODEN': return B.PLANKS_ID;
    case 'STONE': return B.COBBLE_ID;
    case 'IRON': return I.IRON_INGOT;
    case 'GOLDEN': return I.GOLD_INGOT;
    case 'DIAMOND': return I.DIAMOND;
    case 'NETHERITE': return I.NETHERITE_INGOT;
    case 'COPPER': return I.COPPER_INGOT;
    case 'LEATHER': return I.LEATHER;
    default: return 0;
  }
}

/** Is `rightId` a valid unit-repair material for `leftId`? Any plank type repairs wooden tools and shields. */
export function isRepairMaterial(leftId: number, rightId: number): boolean {
  const m = repairMaterial(leftId);
  if (m === 0) return false;
  if (m === B.PLANKS_ID) return PLANK_IDS.includes(rightId);
  if (m === B.COBBLE_ID) return rightId === B.COBBLE_ID || rightId === B.COBBLED_DEEPSLATE_ID; // stone_tool_materials tag
  return m === rightId;
}
const PLANK_IDS: readonly number[] = [B.PLANKS_ID, B.SPRUCE_PLANKS_ID, B.BIRCH_PLANKS_ID, B.JUNGLE_PLANKS_ID, B.ACACIA_PLANKS_ID,
  B.DARK_OAK_PLANKS_ID, B.MANGROVE_PLANKS_ID, B.CHERRY_PLANKS_ID, B.PALE_OAK_PLANKS_ID];

export interface AnvilResult {
  out: Stack;
  /** Level cost. */
  cost: number;
  /** How many of the right-hand stack are consumed. */
  rightUsed: number;
  tooExpensive: boolean;
}

/** `rename`: the text field (undefined = untouched; '' = clear back to the default name). */
export function anvilCombine(left: Stack, right: Stack, creative = false, rename?: string): AnvilResult | null {
  if (isEmpty(left)) return null;
  const out = cloneStack(left);
  let renameCost = 0;
  if (rename !== undefined && rename !== (left.name ?? '')) {
    renameCost = 1;
    if (rename) out.name = rename; else delete out.name;
  }
  if (isEmpty(right)) {
    if (!renameCost) return null;
    const cost = 1 + penalty(left.rep);
    // Bedrock: a rename-only use is never "Too Expensive"
    return { out, cost, rightUsed: 0, tooExpensive: false };
  }
  const max = maxDamageOf(left.id);
  let cost = 0;
  let rightUsed = 1;
  const book = right.id === I.ENCHANTED_BOOK;

  if (max > 0 && right.id !== 0 && isRepairMaterial(left.id, right.id)) {
    // unit repair: 25% of max per material item, 1 level each
    let dmg = out.dmg;
    let used = 0;
    const step = Math.min(dmg, Math.floor(max / 4));
    if (step <= 0) return null;
    while (dmg > 0 && used < right.count) {
      dmg -= Math.min(dmg, Math.floor(max / 4));
      used++;
      cost++;
    }
    out.dmg = dmg;
    rightUsed = used;
  } else {
    if (!book && right.id !== left.id) return null;
    if (!book && max > 0 && left.dmg > 0) {
      const remain = (max - left.dmg) + (max - right.dmg) + Math.floor(max * 12 / 100);
      out.dmg = Math.max(0, max - remain);
      cost += 2;
    }
    let anyApplied = false;
    for (const e of right.ench ?? []) {
      const d = enchDef(e.id);
      if (!d) continue;
      const cur = enchLevel(left, e.id);
      let lvl = cur === e.lvl ? e.lvl + 1 : Math.max(cur, e.lvl);
      lvl = Math.min(lvl, d.max);
      let ok = creative || left.id === I.ENCHANTED_BOOK || canApply(d, left.id);
      for (const o of left.ench ?? []) if (o.id !== e.id && !compatible(o.id, e.id)) ok = false;
      if (!ok) { cost += 1; continue; }
      anyApplied = true;
      setEnch(out, e.id, lvl);
      const mult = book ? Math.max(1, anvilCost(e.id) >> 1) : anvilCost(e.id);
      cost += mult * lvl;
    }
    if (!anyApplied && out.dmg === left.dmg) return null;
  }
  cost += renameCost + penalty(left.rep) + penalty(right.rep);
  out.rep = Math.max(left.rep ?? 0, right.rep ?? 0) + 1;
  if (cost <= 0) return null;
  return { out, cost, rightUsed, tooExpensive: !creative && cost >= TOO_EXPENSIVE };
}
